import { z } from 'zod';
import { Field } from '../sceneDataSchema';
import { dateValidator } from './validators/DateValidator';
import { booleanValidator } from './validators/BooleanValidator';
import { numberValidator } from './validators/NumberValidator';
import { stringValidator } from './validators/StringValidator';
import { DataType, ValidationType } from '@/constants/validationTypes';

interface EntityPath {
  scope: string;
  attribute: string;
  entity_index: number;
}

export function createSchema(inputFields: Field[]) {
  return z.object(
    inputFields.reduce<Record<string, ReturnType<typeof createSchemaForComponent>>>((schemaShape, component) => {
      schemaShape[encodeFieldKey(component)] = createSchemaForComponent(component);
      return schemaShape;
    }, {})
  );
}

function createSchemaForComponent(field: Field) {
  const dataType = extractDataType(field);
  const validations = field.validation_rules;

  switch (dataType) {
    case DataType.String:
      return stringValidator(validations);
    case DataType.Integer:
      return numberValidator(validations);
    case DataType.Boolean:
      return booleanValidator(validations);
    case DataType.Date:
      return dateValidator(validations);
    default:
      // Invalid type "never" of template literal expression.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unsupported data type: ${dataType}`);
  }
}

/**
 * React form hooks needs a unique string name and we need a consistent way to encode these
 * Here I do this using URL search parameters which results in a string that looks like
 * scope=driver&entity_index=1&attribute=firstName. For some odd reason react form hooks does not
 * like JSON.stringify
 * @param param0 The entity triplet we want to encode
 * @returns The encoded url string
 */
export function encodeFieldKey({ scope, entity_index, attribute }: EntityPath): string {
  // we have to convert periods in scope names to something else because react-hook-form will try
  // to nest fields if we leave them.
  scope = scope.replace(/\./, '::');
  return new URLSearchParams({
    scope,
    attribute,
    entity_index: entity_index.toString(), // Convert number to string
  }).toString();
}

/**
 * Decodes a URL search parameter encoded string from `encodeFieldKey`. This decodes a string of the
 * format scope=<scope>&entity_index=<entity_index>&attribute=<attribute> into the triplet it was encoded with
 * @param encodeKey a URL search parameter encoded string
 * @returns An EntityPath which represents the triplet of data
 */
export function decodeFieldKey(encodeKey: string): EntityPath {
  const params = new URLSearchParams(encodeKey);

  // since we have to convert periods in scope names to something else during encoding because
  // react-hook-form will try to nest fields if we leave them, we need to convert them back
  const scope = params.get('scope')?.replace(/::/, '.');
  const attribute = params.get('attribute');
  const entityIndexStr = params.get('entity_index');

  if (!scope || !attribute || entityIndexStr === null) {
    console.error('Missing required URL parameters.');
    throw new Error('Unable to parse encoded key');
  }

  const entity_index = Number(entityIndexStr);
  if (Number.isNaN(entity_index)) {
    console.error('Invalid entity_index: must be a number.');
    throw new Error('Unable to parse encoded key');
  }

  return { scope, attribute, entity_index };
}

/**
 * React form hooks needs unique string name for each field it is modeling. When
 * we need to gather all the information for a multiple drivers or vehicles we run into issues
 * where the keys then become non unique
 *
 * To solves this we now encode a triplet together (scope, attribute, entity_index). This method
 * takes this encoded triplet, decodes it the generates the appropriate nested structure for submission
 * which is now
 *  { "<scope>": { "<entity_index>": { "<attribute1>": "data1", "<attributeN>": "dataN"} } }
 * @param formData This is the encoded form data where our triplet is encoded
 * @returns The structure data to patch to the agency api
 */
export const decodeSubmission = (formData: Record<string, unknown>) => {
  const decoded: Record<string, Record<string, Record<string, unknown>>> = {};

  Object.entries(formData).forEach(([encodedKey, value]) => {
    const { entity_index, scope, attribute } = decodeFieldKey(encodedKey);

    decoded[scope] ||= {};
    decoded[scope][entity_index.toString()] ||= {};
    decoded[scope][entity_index.toString()][attribute] = value;
  });

  return decoded;
};

export function extractDataType(field: Field): DataType {
  for (const rule of field.validation_rules) {
    if (rule.type == ValidationType.DataType) {
      return rule.value;
    }
  }

  throw new Error(`Data type not specified for field ${field.attribute}`);
}
